import { useEffect, useState } from "react";
import { CheckCircle2, AlertCircle, Loader2, ExternalLink, RefreshCw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";
import { useLicense } from "@/hooks/useLicense";
import { useStripeCheckout } from "@/hooks/useStripeCheckout";
import { getStripeEnvironment } from "@/lib/stripe";

type LicenseRow = {
  id: string;
  status: string;
  environment: string;
  product_id: string;
  price_id: string;
  license_key: string | null;
  license_key_sent_at: string | null;
  buyer_email: string | null;
  stripe_session_id: string | null;
  stripe_customer_id: string | null;
  stripe_payment_intent_id: string | null;
  created_at: string;
  updated_at: string;
};

type SessionVerification = {
  status: string | null;
  paymentStatus: string | null;
  amountTotal: number | null;
  currency: string | null;
} | null;

export function LicenseStatusPanel() {
  const { user } = useAuth();
  const { hasLicense, loading: licenseLoading, refetch } = useLicense(user?.id);
  const { openCheckout, checkoutElement } = useStripeCheckout();
  const env = getStripeEnvironment();

  const [license, setLicense] = useState<LicenseRow | null>(null);
  const [loadingLicense, setLoadingLicense] = useState(true);
  const [session, setSession] = useState<SessionVerification>(null);
  const [verifying, setVerifying] = useState(false);

  const loadLicense = async () => {
    if (!user) return;
    setLoadingLicense(true);
    const { data } = await supabase
      .from("licenses")
      .select(
        "id,status,environment,product_id,price_id,license_key,license_key_sent_at,buyer_email,stripe_session_id,stripe_customer_id,stripe_payment_intent_id,created_at,updated_at",
      )
      .eq("user_id", user.id)
      .eq("environment", env)
      .order("created_at", { ascending: false })
      .limit(1)
      .maybeSingle();
    setLicense((data as LicenseRow) ?? null);
    setLoadingLicense(false);
  };

  useEffect(() => {
    loadLicense();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user, env]);

  useEffect(() => {
    if (!license?.stripe_session_id) {
      setSession(null);
      return;
    }
    let cancelled = false;
    setVerifying(true);
    supabase.functions
      .invoke("verify-checkout-session", {
        body: { sessionId: license.stripe_session_id, environment: env },
      })
      .then(({ data, error }) => {
        if (cancelled) return;
        if (error || !data || data.error) {
          setSession(null);
        } else {
          setSession({
            status: data.status,
            paymentStatus: data.paymentStatus,
            amountTotal: data.amountTotal,
            currency: data.currency,
          });
        }
      })
      .finally(() => !cancelled && setVerifying(false));
    return () => {
      cancelled = true;
    };
  }, [license?.stripe_session_id, env]);

  const handleRefresh = async () => {
    await Promise.all([loadLicense(), refetch()]);
  };

  const handleBuy = () => {
    if (!user) return;
    openCheckout({
      priceId: "trends_discovery_lifetime_39",
      customerEmail: user.email ?? undefined,
      userId: user.id,
      returnUrl: `${window.location.origin}/checkout/success?session_id={CHECKOUT_SESSION_ID}`,
    });
  };

  const paid = session?.paymentStatus === "paid";
  const amount =
    session?.amountTotal != null && session.currency
      ? new Intl.NumberFormat("en-US", {
          style: "currency",
          currency: session.currency.toUpperCase(),
        }).format(session.amountTotal / 100)
      : null;

  return (
    <section className="rounded-2xl border border-border bg-card p-6 md:p-8">
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <div className="font-mono text-xs text-primary mb-2">// PAYMENTS · LICENSE</div>
          <h2 className="font-display text-2xl font-bold">Access status</h2>
          <p className="text-sm text-muted-foreground mt-1">
            Stripe environment:{" "}
            <span className="font-mono text-foreground">{env}</span>
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={handleRefresh} disabled={loadingLicense || licenseLoading}>
            <RefreshCw className={`size-4 ${loadingLicense || licenseLoading ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      <div className="mt-6 grid md:grid-cols-2 gap-4">
        {/* License */}
        <div className="rounded-xl border border-border bg-background/40 p-5">
          <div className="font-mono text-[11px] text-muted-foreground mb-3">// LICENSE</div>
          {licenseLoading || loadingLicense ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Loading…
            </div>
          ) : hasLicense ? (
            <div className="flex items-start gap-3">
              <CheckCircle2 className="size-5 text-primary mt-0.5" />
              <div>
                <div className="font-medium">Active</div>
                <div className="text-xs text-muted-foreground mt-1">
                  {license?.product_id} · {license?.price_id}
                </div>
                {license?.license_key && (
                  <div className="mt-3 rounded-lg border border-border bg-background/60 p-3">
                    <div className="font-mono text-[10px] text-muted-foreground">// LICENSE KEY</div>
                    <code
                      className="font-mono text-sm tracking-widest"
                      data-testid="dashboard-license-key"
                    >
                      {license.license_key}
                    </code>
                    <div className="text-[11px] text-muted-foreground mt-1">
                      {license.license_key_sent_at
                        ? `Emailed to ${license.buyer_email ?? "your purchase email"}`
                        : "Emailed to the address used at checkout"}
                    </div>
                  </div>
                )}
                {license?.updated_at && (
                  <div className="text-xs font-mono text-muted-foreground mt-1">
                    updated {new Date(license.updated_at).toLocaleString()}
                  </div>
                )}
              </div>
            </div>

          ) : (
            <div className="flex items-start gap-3">
              <AlertCircle className="size-5 text-muted-foreground mt-0.5" />
              <div className="flex-1">
                <div className="font-medium">No active license</div>
                <div className="text-xs text-muted-foreground mt-1">
                  Buy lifetime access to unlock the full dashboard.
                </div>
                <Button size="sm" variant="hero" className="mt-3" onClick={handleBuy}>
                  Get lifetime access <ExternalLink className="size-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Stripe session */}
        <div className="rounded-xl border border-border bg-background/40 p-5">
          <div className="font-mono text-[11px] text-muted-foreground mb-3">// STRIPE SESSION</div>
          {!license?.stripe_session_id ? (
            <div className="text-sm text-muted-foreground">No checkout session on file yet.</div>
          ) : verifying ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="size-4 animate-spin" /> Verifying with Stripe…
            </div>
          ) : session ? (
            <dl className="grid grid-cols-[auto,1fr] gap-x-3 gap-y-1.5 text-sm font-mono">
              <dt className="text-muted-foreground">session</dt>
              <dd className="truncate" title={license.stripe_session_id}>
                {license.stripe_session_id.slice(0, 22)}…
              </dd>
              <dt className="text-muted-foreground">status</dt>
              <dd>{session.status ?? "—"}</dd>
              <dt className="text-muted-foreground">payment</dt>
              <dd className={paid ? "text-primary" : ""}>{session.paymentStatus ?? "—"}</dd>
              {amount && (
                <>
                  <dt className="text-muted-foreground">amount</dt>
                  <dd>{amount}</dd>
                </>
              )}
              {license.stripe_customer_id && (
                <>
                  <dt className="text-muted-foreground">customer</dt>
                  <dd className="truncate">{license.stripe_customer_id}</dd>
                </>
              )}
            </dl>
          ) : (
            <div className="text-sm text-muted-foreground">
              Could not verify session with Stripe.
            </div>
          )}
        </div>
      </div>

      {checkoutElement}
    </section>
  );
}
